import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { BillingCycle } from 'webapp/enums/enums';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { Space } from 'webapp/models-custom';
import type { SpaceBillingInfo } from 'webapp/models/space';

interface Args {
	model: Space;
}

export default class ClientSubscriptionStatusComponent extends Component<Args> {
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('request') declare requestService: Service.Request;
	@service('account') declare accountService: Service.Account;

	get billingInfo(): SpaceBillingInfo {
		return this.args.model.billingInfo;
	}

	get billingInfoFormattedString() {
		if (this.billingInfo) {
			const planName = this.billingInfo.productName;
			const planPrice = this.billingInfo.totalPriceFormatted.replace('.00', '');
			const planBillingCycle = this.billingInfo.billingCycle;
			const cycle =
				planBillingCycle === BillingCycle.YEARLY
					? 'yr'
					: planBillingCycle === BillingCycle.QUARTERLY
						? 'qt'
						: 'mo';

			return `${planName} (${planPrice}/${cycle})`;
		}
		return '';
	}

	get isPlanExpired() {
		return this.billingInfo.activeUntil
			? this.billingInfo.activeUntil < new Date().getTime() / 1000
			: false;
	}

	get subscriptionStatusInfo(): { icon: IconName; theme: string; title: string } | null {
		if (!this.billingInfo) {
			return null;
		}

		switch (this.billingInfo.subscriptionStatus) {
			case 'trialing':
				return {
					icon: 'Gift',
					theme: this.isPlanExpired ? 'red' : 'blue',
					title: this.isPlanExpired ? 'Trial expired' : 'Trialing',
				};
			case 'canceled':
				return {
					icon: 'Close',
					theme: 'gray',
					title: 'Cancelled',
				};
			case 'active':
				return {
					icon: 'Check',
					theme: 'green',
					title: 'Active',
				};
			case 'paused':
				return {
					icon: 'Pause',
					theme: 'orange',
					title: 'Paused',
				};
			default:
				return {
					icon: 'AlertFill',
					theme: 'red',
					title: 'Payment failed',
				};
		}
	}

	@action
	redirect() {
		const numericId = this.args.model.numericId;
		if (numericId) {
			this.accountService.openAccountAppClientManagerEdit(numericId);
		}
	}
	<template>
		<div>
			<p>{{this.billingInfoFormattedString}}</p>
			{{#if this.subscriptionStatusInfo}}
				<div
					class="subscription-status subscription-status-theme--{{this.subscriptionStatusInfo.theme}}"
				>
					<div class="subscription-status__image">
						<CommonUiIconComponent
							@name={{this.subscriptionStatusInfo.icon}}
							@size="sm"
						/>
					</div>
					<span class="subscription-status__title">
						{{this.subscriptionStatusInfo.title}}
					</span>
				</div>
			{{else}}
				<a
					role="button"
					class="subscription-status subscription-status-theme--blue"
					{{on "click" this.redirect}}
				>
					<CommonUiIconComponent @name="Box" @size="sm" />
					Subscribe account
					<CommonUiIconComponent @name="External" @size="xs" />
					<ToolTipComponent
						@text="This account is currently sharing your plan and usage. Move it to its own subscription to free up capacity, reduce your costs, and earn a 30% commission as a Solutions Partner.<br><a href='https://databox.com/reseller' target='_blank'>Learn more</a>"
						@interactive={{true}}
						@maxWidth={{320}}
						@theme="light"
					/>
				</a>
			{{/if}}
		</div>
	</template>
}
